// src/features/faq/FaqUpsertDialog.tsx
import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
} from "@mui/material";
import type { FaqItem } from "src/store/faq";

// faqId는 수정 시에만 존재
export type FaqUpsertPayload = {
  faqId?: number;
  faqType: string;
  faqTtl: string;
  faqCts: string;
  activeYn: boolean;
  faqOrder: number;
};

type Props = {
  open: boolean;
  item: FaqItem | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: FaqUpsertPayload) => Promise<void>;
};
const FaqUpsertDialog = ({ open, item, loading = false, onClose, onSubmit }: Props) => {
  const [faqType, setFaqType] = React.useState("");
  const [faqTtl, setFaqTtl] = React.useState("");
  const [faqCts, setFaqCts] = React.useState("");
  const [activeYn, setActiveYn] = React.useState(true);
  const [faqOrder, setFaqOrder] = React.useState<number>(0);

  React.useEffect(() => {
    if (!open) return;

    if (item) {
      setFaqType(item.faqType ?? "");
      setFaqTtl(item.faqTtl ?? "");
      setFaqCts(item.faqCts ?? "");
      setActiveYn(!!item.activeYn);
      setFaqOrder(Number.isFinite(item.faqOrder) ? item.faqOrder : 0);
    } else {
      setFaqType("");
      setFaqTtl("");
      setFaqCts("");
      setActiveYn(true);
      setFaqOrder(0);
    }
  }, [open, item]);

  const disabled =
    loading || !faqType.trim() || !faqTtl.trim() || !faqCts.trim() || !Number.isFinite(faqOrder);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>{item ? "FAQ 수정" : "FAQ 등록"}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="FAQ 타입"
            value={faqType}
            onChange={(e) => setFaqType(e.target.value)}
            disabled={loading}
            fullWidth
            size="small"
          />

          <TextField
            label="제목"
            value={faqTtl}
            onChange={(e) => setFaqTtl(e.target.value)}
            disabled={loading}
            fullWidth
            size="small"
          />

          <TextField
            label="내용"
            value={faqCts}
            onChange={(e) => setFaqCts(e.target.value)}
            disabled={loading}
            fullWidth
            size="small"
            multiline
            minRows={4}
          />

          <TextField
            label="노출 순서 (faqOrder)"
            type="number"
            value={faqOrder}
            onChange={(e) => setFaqOrder(Number(e.target.value))}
            disabled={loading}
            fullWidth
            size="small"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={activeYn}
                onChange={(e) => setActiveYn(e.target.checked)}
                disabled={loading}
              />
            }
            label="활성"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          취소
        </Button>
        <Button
          onClick={async () => {
            await onSubmit({
              faqId: item?.faqId,
              faqType: faqType.trim(),
              faqTtl: faqTtl.trim(),
              faqCts: faqCts.trim(),
              activeYn,
              faqOrder: Number.isFinite(faqOrder) ? faqOrder : 0,
            });
          }}
          disabled={disabled}
          variant="contained"
        >
          {loading ? "저장 중..." : "저장"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaqUpsertDialog;
